import React, { useState, forwardRef } from 'react';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { cn } from './ui/utils';

export interface PeopleSelectProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  onAddPerson: (name: string) => void;
  allowNone?: boolean;
  placeholder?: string;
  className?: string;
}

const SelectButton = forwardRef<HTMLButtonElement, any>(({ value, placeholder, allowNone, onClear, open, className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    role="combobox"
    aria-expanded={open}
    className={cn(
      'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
      className
    )}
    {...props}
  >
    <span className="truncate">{value || placeholder}</span>
    <div className="flex items-center gap-1 ml-2">
      {value && allowNone && (
        <X 
          className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100" 
          onClick={onClear}
        />
      )}
      <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
    </div>
  </button>
));

SelectButton.displayName = 'SelectButton';

export function PeopleSelect({
  value,
  options,
  onChange,
  onAddPerson,
  allowNone = false,
  placeholder = 'Select person...',
  className,
}: PeopleSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(search.toLowerCase())
  );

  const showCreateOption = search.trim() !== '' && 
    !options.some(opt => opt.toLowerCase() === search.toLowerCase());

  const handleSelect = (selectedValue: string) => {
    if (allowNone && selectedValue === value) {
      onChange('');
    } else {
      onChange(selectedValue);
    }
    setOpen(false);
    setSearch('');
  };

  const handleCreate = () => {
    if (search.trim()) {
      const trimmedName = search.trim();
      onAddPerson(trimmedName);
      onChange(trimmedName);
      setOpen(false);
      setSearch('');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SelectButton
          value={value}
          placeholder={placeholder}
          allowNone={allowNone}
          onClear={handleClear}
          open={open}
          className={className}
        />
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="start">
        <div className="max-h-[300px] overflow-auto">
          {/* Search Input */}
          <div className="sticky top-0 bg-popover p-2 border-b">
            <Input
              type="text"
              placeholder="Search or add new person..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
            />
          </div>

          {/* Options List */}
          <div className="p-1">
            {/* None Option */}
            {allowNone && (
              <button
                type="button"
                onClick={() => handleSelect('')}
                className={cn(
                  "w-full flex items-center px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer",
                  value === '' && "bg-accent"
                )}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value === '' ? 'opacity-100' : 'opacity-0'
                  )}
                />
                <span className="text-muted-foreground italic">None</span>
              </button>
            )}

            {/* Filtered Options */}
            {filteredOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleSelect(option)}
                className={cn(
                  "w-full flex items-center px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer",
                  value === option && "bg-accent"
                )}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value === option ? 'opacity-100' : 'opacity-0'
                  )}
                />
                {option}
              </button>
            ))}

            {/* Create Option */}
            {showCreateOption && (
              <button
                type="button"
                onClick={handleCreate}
                className="w-full flex items-center px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer text-primary border-t mt-1 pt-2"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add "{search}"
              </button>
            )}

            {/* Empty State */}
            {filteredOptions.length === 0 && !showCreateOption && !allowNone && (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                No people found.
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
